import type {
    GroupedTaskBatchesScanResult,
    OverallTaskHistoryResult,
    ProjectTaskBatchesResult, ScanPeriod,
    SingleTaskBatchScanResult, TaskOverlaps, TaskSourceBreakdown
} from "../../scan-model/GroupedTaskBatchesScanResult.tsx";
import {calculateBatchOverlaps, type BatchOverlapResult} from "./BatchOverlapCalculator.tsx";

function sum(a: Record<number, number>[]): Record<number, number> {
    return a.reduce((accumulator, currentValue) => {
        Object.entries(currentValue).forEach(([k, v]) => {
            const oldValue = accumulator[parseInt(k)] ?? 0
            accumulator[parseInt(k)] = oldValue + v
        })
        return accumulator
    }, {} as Record<number, number>)
}

function sumStr(a: Record<string, number>[]): Record<string, number> {
    return a.reduce((accumulator, currentValue) => {
        Object.entries(currentValue).forEach(([k, v]) => {
            const oldValue = accumulator[k] ?? 0
            accumulator[k] = oldValue + v
        })
        return accumulator
    }, {} as Record<string, number>)
}

function mergeScanPeriod(periods: ScanPeriod[]): ScanPeriod {
    const min = Math.min(...periods.map(x => x.start.getTime()))
    const max = Math.max(...periods.map(x => x.end.getTime()))
    return {
        start: new Date(min),
        end: new Date(max),
        duration: max - min
    }
}

function mergeOverlaps(projects: ProjectTaskBatchesResult[], span: ScanPeriod): TaskOverlaps {
    const totalBlobDurations = projects.map(p => p.totalRunningTime).reduce((a, b) => a + b, 0)
    return projects.reduce((a, b) => {
        const compRunTime = b.overlaps.meanOverlap * b.totalRunningTime
        a.meanOverlap += compRunTime / totalBlobDurations
        a.highestOverlap = Math.max(a.highestOverlap, b.overlaps.highestOverlap)
        a.meanOverallOverlap += compRunTime / span.duration
        return a
    }, {meanOverlap: 0, highestOverlap: 0, meanOverallOverlap: 0})
}

function mergeTaskSources(projects: ProjectTaskBatchesResult[]): TaskSourceBreakdown {
    const intermediate = sumStr(projects.map(x => x.taskSources) as unknown as Record<string, number>[])
    return {
        api: intermediate.api,
        manual: intermediate.manual,
        queue: intermediate.queue,
        scheduled: intermediate.scheduled
    }
}

function group(projects: ProjectTaskBatchesResult[], totalOverlap: BatchOverlapResult): GroupedTaskBatchesScanResult {
    const period = mergeScanPeriod(projects.map(p=>p.scanPeriod))
    return {
        componentImplementationRowCounts: sum(projects.map(p=>p.componentImplementationRowCounts)),
        componentImplementationTimes: sum(projects.map(p=>p.componentImplementationTimes)),
        componentImplementationRuns: sum(projects.map(p=>p.componentImplementationRuns)),
        jobTimes: sumStr(projects.map(p=>p.jobTimes)),
        numberOfTaskBatches: projects.map(p=>p.numberOfTaskBatches).reduce((a,b)=>a+b, 0),
        scanPeriod: period,
        overlaps: mergeOverlaps(projects, period),
        batchOverlaps: totalOverlap,
        taskSources: mergeTaskSources(projects),
        totalRunningTime: projects.map(p=>p.totalRunningTime).reduce((a,b)=>a+b, 0)
    }
}

export function calculateOverallTaskResults(
    results: [ProjectTaskBatchesResult, SingleTaskBatchScanResult[]][]
): OverallTaskHistoryResult {
    const totalIndividuals = results.map(([,b])=>b).flat()
    const projectResults = results.map(([a])=>a)
    const totalOverlap = calculateBatchOverlaps(totalIndividuals)
    return {
        overall: group(projectResults, totalOverlap),
        projects: projectResults,
        individualResults: totalIndividuals
    }
}